import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { QrCodeIcon, CreditCardIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, SearchIcon, RefreshCwIcon, ShieldAlertIcon } from 'lucide-react';
export function SmartIdRfid() {
  const [activeTab, setActiveTab] = useState<'cards' | 'issue' | 'verify' | 'reissue'>('cards');
  const [currentStep, setCurrentStep] = useState(0);
  const [verificationResult, setVerificationResult] = useState<'valid' | 'invalid' | null>(null);
  // Re-issue state
  const [reissueSearch, setReissueSearch] = useState('');
  const [reissueFound, setReissueFound] = useState<any | null>(null);
  const [reissueReason, setReissueReason] = useState('lost');
  const [reissueConfirmed, setReissueConfirmed] = useState(false);
  const [cardsList, setCardsList] = useState([{
    cardId: 'CARD-2024-001',
    name: 'Rahim Uddin',
    nid: '1234567890123',
    type: 'QR + RFID',
    issueDate: '2024-01-15',
    expiry: '2025-01-15',
    status: 'active' as const
  }, {
    cardId: 'CARD-2024-002',
    name: 'Karim Sheikh',
    nid: '2345678901234',
    type: 'QR Code',
    issueDate: '2024-02-10',
    expiry: '2025-02-10',
    status: 'active' as const
  }, {
    cardId: 'CARD-2024-003',
    name: 'Fatema Begum',
    nid: '3456789012345',
    type: 'RFID',
    issueDate: '2024-02-20',
    expiry: '2025-02-20',
    status: 'active' as const
  }, {
    cardId: 'CARD-2024-004',
    name: 'Abdul Hakim',
    nid: '4567890123456',
    type: 'QR + RFID',
    issueDate: '2023-12-01',
    expiry: '2024-12-01',
    status: 'expired' as const
  }, {
    cardId: 'CARD-2024-005',
    name: 'Jamal Hossain',
    nid: '5678901234567',
    type: 'QR Code',
    issueDate: '2024-01-20',
    expiry: '2024-03-15',
    status: 'lost' as const
  }]);
  const columns = [{
    key: 'cardId',
    label: 'Card ID'
  }, {
    key: 'name',
    label: 'Beneficiary Name'
  }, {
    key: 'nid',
    label: 'NID'
  }, {
    key: 'type',
    label: 'Card Type'
  }, {
    key: 'issueDate',
    label: 'Issue Date'
  }, {
    key: 'expiry',
    label: 'Expiry'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-2220" />
  }];
  const filters = [{
    key: 'type',
    label: 'Card Type',
    options: ['QR Code', 'RFID', 'QR + RFID']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Active', 'Expired', 'Lost', 'Replaced']
  }];
  const wizardSteps = [{
    title: 'Select Beneficiary',
    description: 'Choose approved beneficiary'
  }, {
    title: 'Card Configuration',
    description: 'Configure card type and settings'
  }, {
    title: 'Review & Print',
    description: 'Review and print the card'
  }];
  const handleReissueSearch = () => {
    const found = cardsList.find(c => c.cardId.toLowerCase() === reissueSearch.toLowerCase() || c.nid === reissueSearch);
    setReissueFound(found || 'not_found');
    setReissueConfirmed(false);
  };
  const handleReissue = () => {
    if (!reissueFound || reissueFound === 'not_found') return;
    // Mark old card as lost/damaged
    const updatedCards = cardsList.map(c => c.cardId === reissueFound.cardId ? {
      ...c,
      status: reissueReason === 'lost' ? 'lost' as const : 'expired' as const
    } : c);
    // Create new replacement card
    const newCardId = `CARD-${new Date().getFullYear()}-${String(cardsList.length + 1).padStart(3, '0')}`;
    const today = new Date().toISOString().split('T')[0];
    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    const newCard = {
      cardId: newCardId,
      name: reissueFound.name,
      nid: reissueFound.nid,
      type: reissueFound.type,
      issueDate: today,
      expiry: expiryDate.toISOString().split('T')[0],
      status: 'active' as const
    };
    setCardsList([newCard, ...updatedCards]);
    setReissueConfirmed(true);
  };
  const resetReissue = () => {
    setReissueSearch('');
    setReissueFound(null);
    setReissueConfirmed(false);
    setReissueReason('lost');
  };
  return <div className="space-y-6" data-id="element-2221">
      <div data-id="element-2222">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2223">Smart ID & RFID</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2224">
          Manage smart ID cards and RFID verification
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-2225">
        <div className="flex gap-4" data-id="element-2226">
          <button onClick={() => setActiveTab('cards')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'cards' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2227">
            Card Registry
          </button>
          <button onClick={() => setActiveTab('issue')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'issue' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2228">
            Issue New Card
          </button>
          <button onClick={() => {
          setActiveTab('reissue');
          resetReissue();
        }} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'reissue' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2229">
            Lost Card / Re-issue
          </button>
          <button onClick={() => setActiveTab('verify')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'verify' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2230">
            Verify Card
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'cards' && <DataTable columns={columns} data={cardsList} filters={filters} onDataChange={setCardsList} searchPlaceholder="Search cards..." data-id="element-2231" />}

      {activeTab === 'issue' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      alert('Card issued successfully!');
      setCurrentStep(0);
      setActiveTab('cards');
    }} data-id="element-2232">
          {currentStep === 0 && <div className="space-y-4" data-id="element-2233">
              <div data-id="element-2234">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2235">
                  Search Approved Beneficiary *
                </label>
                <input type="text" placeholder="Enter NID or Name" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2236" />
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-2237">
                <div className="flex items-center gap-4" data-id="element-2238">
                  <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center text-2xl" data-id="element-2239">
                    👤
                  </div>
                  <div className="flex-1" data-id="element-2240">
                    <h4 className="font-semibold text-gray-900" data-id="element-2241">Rahim Uddin</h4>
                    <div className="text-sm text-gray-600 mt-1" data-id="element-2242">
                      <div data-id="element-2243">NID: 1234567890123</div>
                      <div data-id="element-2244">Profession: Jele (Fisher)</div>
                      <div data-id="element-2245">
                        Status: <StatusBadge status="approved" data-id="element-2246" />
                      </div>
                    </div>
                  </div>
                  <button className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32]" data-id="element-2247">
                    Select
                  </button>
                </div>
              </div>
            </div>}
          {currentStep === 1 && <div className="space-y-4" data-id="element-2248">
              <div data-id="element-2249">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2250">
                  Card Type *
                </label>
                <div className="grid grid-cols-1 max-w-xs" data-id="element-2251">
                  <label className="border-2 border-[#1B5E20] bg-green-50 rounded-lg p-4 cursor-pointer" data-id="element-2252">
                    <input type="radio" name="cardType" value="rfid" className="sr-only" defaultChecked data-id="element-2253" />
                    <div className="text-center" data-id="element-2254">
                      <CreditCardIcon className="mx-auto mb-2 text-[#1B5E20]" size={32} data-id="element-2255" />
                      <div className="font-medium" data-id="element-2256">RFID</div>
                    </div>
                  </label>
                </div>
              </div>
              <div className="bg-gray-100 rounded-lg p-6 border-2 border-dashed border-gray-300" data-id="element-2257">
                <div className="text-center text-gray-600" data-id="element-2258">
                  <div className="text-sm font-medium mb-2" data-id="element-2259">
                    Card Template Preview
                  </div>
                  <div className="bg-white rounded-lg p-4 inline-block" data-id="element-2260">
                    <div className="w-64 h-40 border-2 border-gray-300 rounded-lg flex items-center justify-center" data-id="element-2261">
                      <div className="text-center" data-id="element-2262">
                        <div className="font-bold text-lg mb-2" data-id="element-2263">
                          Bangladesh Forest Dept.
                        </div>
                        <div className="text-sm" data-id="element-2264">Rahim Uddin</div>
                        <div className="text-xs text-gray-600 mt-1" data-id="element-2265">
                          ID: CARD-2024-XXX
                        </div>
                        <div className="mt-3 w-20 h-20 bg-gray-200 mx-auto" data-id="element-2266"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {currentStep === 2 && <div className="space-y-6" data-id="element-2267">
              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2268">
                <h4 className="font-semibold text-gray-900 mb-4" data-id="element-2269">
                  Card Preview
                </h4>
                <div className="space-y-6" data-id="element-2270">
                  {/* Front Side */}
                  <div data-id="element-2271">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-2272">
                      Front Side
                    </p>
                    <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 max-w-2xl mx-auto" data-id="element-2273">
                      <img src="https://cdn.magicpatterns.com/uploads/pqj4eXKmV692dt1h5NizSK/image.png" alt="Smart ID Card - Front" className="w-full h-auto object-contain" data-id="element-2274" />
                    </div>
                  </div>
                  {/* Back Side */}
                  <div data-id="element-2275">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-2276">
                      Back Side
                    </p>
                    <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 max-w-2xl mx-auto" data-id="element-2277">
                      <img src="https://cdn.magicpatterns.com/uploads/3k2tX1SLhegMkNKt1LT53X/BackCard(1).png" alt="Smart ID Card - Back" className="w-full h-auto object-contain" data-id="element-2278" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2279">
                <button className="px-6 py-3 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] font-medium" data-id="element-2280">
                  Print Card
                </button>
              </div>
            </div>}
        </WizardForm>}

      {activeTab === 'reissue' && <div className="max-w-2xl mx-auto space-y-6" data-id="element-2281">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-id="element-2282">
            <AlertTriangleIcon className="text-amber-600 mt-0.5" size={20} data-id="element-2283" />
            <div data-id="element-2284">
              <h4 className="font-semibold text-amber-900" data-id="element-2285">
                Lost / Damaged Card Re-issue
              </h4>
              <p className="text-sm text-amber-700 mt-1" data-id="element-2286">
                Search for the original card by Card ID or NID. The old card
                will be deactivated and a new replacement card will be issued.
              </p>
            </div>
          </div>

          {!reissueConfirmed && <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4" data-id="element-2287">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-2288">
                Search Original Card
              </h3>
              <div className="flex gap-3" data-id="element-2289">
                <div className="flex-1" data-id="element-2290">
                  <input type="text" value={reissueSearch} onChange={e => setReissueSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleReissueSearch()} placeholder="Enter Card ID (e.g. CARD-2024-001) or NID" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2291" />
                </div>
                <button onClick={handleReissueSearch} disabled={!reissueSearch.trim()} className="px-5 py-2.5 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2292">
                  <SearchIcon size={16} data-id="element-2293" />
                  Search
                </button>
              </div>

              {reissueFound === 'not_found' && <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3" data-id="element-2294">
                  <XCircleIcon className="text-red-600" size={20} data-id="element-2295" />
                  <p className="text-sm text-red-800 font-medium" data-id="element-2296">
                    No card found matching "{reissueSearch}". Please check the
                    Card ID or NID and try again.
                  </p>
                </div>}

              {reissueFound && reissueFound !== 'not_found' && <div className="space-y-4" data-id="element-2297">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-2298">
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-2299">
                      Card Found
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2300">
                      <div data-id="element-2301">
                        <span className="text-gray-500" data-id="element-2302">Card ID:</span>
                        <span className="ml-2 font-semibold text-gray-900" data-id="element-2303">
                          {reissueFound.cardId}
                        </span>
                      </div>
                      <div data-id="element-2304">
                        <span className="text-gray-500" data-id="element-2305">Name:</span>
                        <span className="ml-2 font-semibold text-gray-900" data-id="element-2306">
                          {reissueFound.name}
                        </span>
                      </div>
                      <div data-id="element-2307">
                        <span className="text-gray-500" data-id="element-2308">NID:</span>
                        <span className="ml-2 font-medium text-gray-900" data-id="element-2309">
                          {reissueFound.nid}
                        </span>
                      </div>
                      <div data-id="element-2310">
                        <span className="text-gray-500" data-id="element-2311">Card Type:</span>
                        <span className="ml-2 font-medium text-gray-900" data-id="element-2312">
                          {reissueFound.type}
                        </span>
                      </div>
                      <div data-id="element-2313">
                        <span className="text-gray-500" data-id="element-2314">Issue Date:</span>
                        <span className="ml-2 text-gray-900" data-id="element-2315">
                          {reissueFound.issueDate}
                        </span>
                      </div>
                      <div data-id="element-2316">
                        <span className="text-gray-500" data-id="element-2317">Current Status:</span>
                        <span className="ml-2" data-id="element-2318">
                          <StatusBadge status={reissueFound.status} data-id="element-2319" />
                        </span>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-2320">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2321">
                      Reason for Re-issue *
                    </label>
                    <div className="flex gap-4" data-id="element-2322">
                      <label className={`flex-1 border-2 rounded-lg p-3 cursor-pointer text-center transition-colors ${reissueReason === 'lost' ? 'border-[#1B5E20] bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2323">
                        <input type="radio" name="reissueReason" value="lost" checked={reissueReason === 'lost'} onChange={() => setReissueReason('lost')} className="sr-only" data-id="element-2324" />
                        <AlertTriangleIcon size={20} className={`mx-auto mb-1 ${reissueReason === 'lost' ? 'text-[#1B5E20]' : 'text-gray-400'}`} data-id="element-2325" />
                        <div className={`text-sm font-medium ${reissueReason === 'lost' ? 'text-[#1B5E20]' : 'text-gray-600'}`} data-id="element-2326">
                          Lost
                        </div>
                      </label>
                      <label className={`flex-1 border-2 rounded-lg p-3 cursor-pointer text-center transition-colors ${reissueReason === 'damaged' ? 'border-[#1B5E20] bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2327">
                        <input type="radio" name="reissueReason" value="damaged" checked={reissueReason === 'damaged'} onChange={() => setReissueReason('damaged')} className="sr-only" data-id="element-2328" />
                        <XCircleIcon size={20} className={`mx-auto mb-1 ${reissueReason === 'damaged' ? 'text-[#1B5E20]' : 'text-gray-400'}`} data-id="element-2329" />
                        <div className={`text-sm font-medium ${reissueReason === 'damaged' ? 'text-[#1B5E20]' : 'text-gray-600'}`} data-id="element-2330">
                          Damaged
                        </div>
                      </label>
                      <label className={`flex-1 border-2 rounded-lg p-3 cursor-pointer text-center transition-colors ${reissueReason === 'stolen' ? 'border-[#1B5E20] bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2331">
                        <input type="radio" name="reissueReason" value="stolen" checked={reissueReason === 'stolen'} onChange={() => setReissueReason('stolen')} className="sr-only" data-id="element-2332" />
                        <ShieldAlertIcon size={20} className={`mx-auto mb-1 ${reissueReason === 'stolen' ? 'text-[#1B5E20]' : 'text-gray-400'}`} data-id="element-2333" />
                        <div className={`text-sm font-medium ${reissueReason === 'stolen' ? 'text-[#1B5E20]' : 'text-gray-600'}`} data-id="element-2334">
                          Stolen
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-2335">
                    <p className="text-xs text-red-700" data-id="element-2336">
                      <strong data-id="element-2337">Warning:</strong> Proceeding will permanently
                      deactivate card <strong data-id="element-2338">{reissueFound.cardId}</strong> and
                      issue a new replacement card. This action cannot be
                      undone.
                    </p>
                  </div>

                  <div className="flex justify-end gap-3" data-id="element-2339">
                    <button onClick={resetReissue} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium" data-id="element-2340">
                      Cancel
                    </button>
                    <button onClick={handleReissue} className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center gap-2 text-sm" data-id="element-2341">
                      <RefreshCwIcon size={16} data-id="element-2342" />
                      Deactivate & Re-issue
                    </button>
                  </div>
                </div>}
            </div>}

          {reissueConfirmed && <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6" data-id="element-2343">
              <div className="flex items-start gap-4" data-id="element-2344">
                <CheckCircleIcon className="text-green-600 flex-shrink-0" size={48} data-id="element-2345" />
                <div className="flex-1" data-id="element-2346">
                  <h4 className="text-xl font-bold text-green-900 mb-2" data-id="element-2347">
                    Card Re-issued Successfully
                  </h4>
                  <div className="space-y-2 text-sm text-green-900" data-id="element-2348">
                    <div data-id="element-2349">
                      <span className="font-semibold" data-id="element-2350">Old Card:</span>{' '}
                      {reissueFound.cardId} — <StatusBadge status="lost" data-id="element-2351" />
                    </div>
                    <div data-id="element-2352">
                      <span className="font-semibold" data-id="element-2353">New Card:</span>{' '}
                      {cardsList[0]?.cardId} — <StatusBadge status="active" data-id="element-2354" />
                    </div>
                    <div data-id="element-2355">
                      <span className="font-semibold" data-id="element-2356">Beneficiary:</span>{' '}
                      {reissueFound.name}
                    </div>
                    <div data-id="element-2357">
                      <span className="font-semibold" data-id="element-2358">Reason:</span>{' '}
                      {reissueReason.charAt(0).toUpperCase() + reissueReason.slice(1)}
                    </div>
                  </div>
                  <div className="mt-4 flex gap-3" data-id="element-2359">
                    <button onClick={() => {
                resetReissue();
                setActiveTab('cards');
              }} className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium" data-id="element-2360">
                      View Card Registry
                    </button>
                    <button onClick={resetReissue} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium" data-id="element-2361">
                      Re-issue Another
                    </button>
                  </div>
                </div>
              </div>
            </div>}
        </div>}

      {activeTab === 'verify' && <div className="max-w-2xl mx-auto" data-id="element-2362">
          <div className="bg-white rounded-lg border border-gray-200 p-8" data-id="element-2363">
            <h3 className="text-xl font-semibold text-gray-900 text-center mb-6" data-id="element-2364">
              Verify Smart ID Card
            </h3>

            <div className="space-y-4 mb-8" data-id="element-2365">
              <button onClick={() => setVerificationResult('valid')} className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] transition-colors" data-id="element-2366">
                <QrCodeIcon size={24} data-id="element-2367" />
                <span className="font-medium" data-id="element-2368">Scan QR Code</span>
              </button>

              <div className="text-center text-sm text-gray-600" data-id="element-2369">OR</div>

              <input type="text" placeholder="Enter Card ID manually" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2370" />

              <div className="text-center text-sm text-gray-600" data-id="element-2371">OR</div>

              <button onClick={() => setVerificationResult('invalid')} className="w-full flex items-center justify-center gap-3 px-6 py-4 border-2 border-[#1B5E20] text-[#1B5E20] rounded-lg hover:bg-green-50 transition-colors" data-id="element-2372">
                <CreditCardIcon size={24} data-id="element-2373" />
                <span className="font-medium" data-id="element-2374">Tap RFID Card</span>
              </button>
            </div>

            {verificationResult && <div className={`rounded-lg p-6 ${verificationResult === 'valid' ? 'bg-green-50 border-2 border-green-500' : 'bg-red-50 border-2 border-red-500'}`} data-id="element-2375">
                <div className="flex items-start gap-4" data-id="element-2376">
                  {verificationResult === 'valid' ? <CheckCircleIcon className="text-green-600 flex-shrink-0" size={48} data-id="element-2377" /> : <XCircleIcon className="text-red-600 flex-shrink-0" size={48} data-id="element-2378" />}
                  <div className="flex-1" data-id="element-2379">
                    <h4 className={`text-xl font-bold mb-2 ${verificationResult === 'valid' ? 'text-green-900' : 'text-red-900'}`} data-id="element-2380">
                      {verificationResult === 'valid' ? 'Valid Card' : 'Invalid Card'}
                    </h4>
                    {verificationResult === 'valid' ? <div className="space-y-2 text-sm text-green-900" data-id="element-2381">
                        <div data-id="element-2382">
                          <span className="font-semibold" data-id="element-2383">Name:</span> Rahim
                          Uddin
                        </div>
                        <div data-id="element-2384">
                          <span className="font-semibold" data-id="element-2385">Card ID:</span>{' '}
                          CARD-2024-001
                        </div>
                        <div data-id="element-2386">
                          <span className="font-semibold" data-id="element-2387">Profession:</span>{' '}
                          Jele (Fisher)
                        </div>
                        <div data-id="element-2388">
                          <span className="font-semibold" data-id="element-2389">Active Permit:</span>{' '}
                          PER-2024-001 (Fishing, Valid until 2024-04-05)
                        </div>
                        <div data-id="element-2390">
                          <span className="font-semibold" data-id="element-2391">Vessel Name:</span> MV
                          Sundarban-1
                        </div>
                        <div data-id="element-2392">
                          <span className="font-semibold" data-id="element-2393">BLC No:</span>{' '}
                          BLC-2024-8821
                        </div>
                        <div data-id="element-2394">
                          <span className="font-semibold" data-id="element-2395">Status:</span>{' '}
                          <StatusBadge status="active" data-id="element-2396" />
                        </div>
                      </div> : <div className="text-sm text-red-900" data-id="element-2397">
                        <div className="font-semibold mb-1" data-id="element-2398">
                          Reason: Card Expired
                        </div>
                        <div data-id="element-2399">
                          This card expired on 2024-03-15. Please renew the card
                          before entry.
                        </div>
                      </div>}
                  </div>
                </div>
              </div>}
          </div>
        </div>}
    </div>;
}